import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  NoSymbolIcon,
  PlusIcon,
  ShieldCheckIcon,
  TruckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import toast, { Toaster } from 'react-hot-toast';
import { format } from 'date-fns';
import type {
  AmbulanceFleetBlockedBy,
  AmbulanceFleetDriver,
  AmbulanceFleetVehicle,
} from '../../../services/MasterAdmin/ambulanceService';
import {
  blockAmbulanceFleetDriver,
  blockAmbulanceFleetVehicle,
  createAmbulanceFleetDriver,
  createAmbulanceFleetVehicle,
  fetchAmbulanceFleetDrivers,
  fetchAmbulanceFleetVehicles,
  unblockAmbulanceFleetDriver,
  unblockAmbulanceFleetVehicle,
} from '../../../services/MasterAdmin/ambulanceService';

type TabKey = 'drivers' | 'ambulances';
type BlockDialogTarget =
  | { type: 'driver'; row: AmbulanceFleetDriver }
  | { type: 'vehicle'; row: AmbulanceFleetVehicle };

function blockedByLabel(value: AmbulanceFleetBlockedBy): string {
  if (value === 'master_admin') return 'Master admin';
  if (value === 'ambulance_provider') return 'Ambulance provider';
  return '—';
}

function statusBadge(row: { isBlocked: boolean; isActive: boolean }) {
  if (row.isBlocked) {
    return (
      <span className="inline-flex rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 px-2 py-0.5 text-xs font-medium">
        Blocked
      </span>
    );
  }
  if (row.isActive) {
    return (
      <span className="inline-flex rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 px-2 py-0.5 text-xs font-medium">
        Active
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 px-2 py-0.5 text-xs font-medium">
      Inactive
    </span>
  );
}

const AmbulanceVendorFleetPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('drivers');
  const [drivers, setDrivers] = useState<AmbulanceFleetDriver[]>([]);
  const [vehicles, setVehicles] = useState<AmbulanceFleetVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [driverName, setDriverName] = useState('');
  const [driverPhone, setDriverPhone] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [blockDialogTarget, setBlockDialogTarget] = useState<BlockDialogTarget | null>(null);

  const loadDrivers = useCallback(async () => {
    if (!vendorId) return;
    const res = await fetchAmbulanceFleetDrivers(vendorId);
    setDrivers(Array.isArray(res.data) ? res.data : []);
  }, [vendorId]);

  const loadVehicles = useCallback(async () => {
    if (!vendorId) return;
    const res = await fetchAmbulanceFleetVehicles(vendorId);
    setVehicles(Array.isArray(res.data) ? res.data : []);
  }, [vendorId]);

  const load = useCallback(async () => {
    if (!vendorId) return;
    try {
      setLoading(true);
      await Promise.all([loadDrivers(), loadVehicles()]);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load drivers and ambulances.');
    } finally {
      setLoading(false);
    }
  }, [vendorId, loadDrivers, loadVehicles]);

  useEffect(() => {
    load();
  }, [load]);

  const openAddModal = () => {
    setDriverName('');
    setDriverPhone('');
    setVehiclePlate('');
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!vendorId) return;
    try {
      setSaving(true);
      if (tab === 'drivers') {
        const name = driverName.trim();
        const phone = driverPhone.replace(/\D/g, '');
        if (!name) {
          toast.error('Driver name is required.');
          return;
        }
        if (phone.length < 10) {
          toast.error('Enter a valid 10-digit phone number.');
          return;
        }
        await createAmbulanceFleetDriver(vendorId, { name, phoneNumber: phone });
        toast.success('Driver added.');
        await loadDrivers();
      } else {
        const numberPlate = vehiclePlate.trim();
        if (!numberPlate) {
          toast.error('Number plate is required.');
          return;
        }
        await createAmbulanceFleetVehicle(vendorId, { numberPlate });
        toast.success('Ambulance added.');
        await loadVehicles();
      }
      setModalOpen(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const toggleDriverBlock = async (row: AmbulanceFleetDriver) => {
    if (!vendorId) return;
    const id = row.driverId;
    try {
      setActionId(id);
      if (row.isBlocked) {
        await unblockAmbulanceFleetDriver(vendorId, id);
        toast.success('Driver unblocked.');
      } else {
        setBlockDialogTarget({ type: 'driver', row });
        return;
      }
      await loadDrivers();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Action failed.');
    } finally {
      setActionId(null);
    }
  };

  const toggleVehicleBlock = async (row: AmbulanceFleetVehicle) => {
    if (!vendorId) return;
    const id = row.vehicleId;
    try {
      setActionId(id);
      if (row.isBlocked) {
        await unblockAmbulanceFleetVehicle(vendorId, id);
        toast.success('Ambulance unblocked.');
      } else {
        setBlockDialogTarget({ type: 'vehicle', row });
        return;
      }
      await loadVehicles();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Action failed.');
    } finally {
      setActionId(null);
    }
  };

  const confirmBlock = async () => {
    if (!vendorId || !blockDialogTarget) return;
    try {
      const rowId =
        blockDialogTarget.type === 'driver'
          ? blockDialogTarget.row.driverId
          : blockDialogTarget.row.vehicleId;
      setActionId(rowId);
      if (blockDialogTarget.type === 'driver') {
        await blockAmbulanceFleetDriver(vendorId, blockDialogTarget.row.driverId);
        toast.success('Driver blocked.');
        await loadDrivers();
      } else {
        await blockAmbulanceFleetVehicle(vendorId, blockDialogTarget.row.vehicleId);
        toast.success('Ambulance blocked.');
        await loadVehicles();
      }
      setBlockDialogTarget(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Action failed.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-5xl mx-auto p-3 sm:p-4 md:p-6">
      <Toaster position="top-right" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(`/vendors/ambulance/${vendorId}`)}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to vendor dashboard
        </button>
        <button
          type="button"
          onClick={openAddModal}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          {tab === 'drivers' ? 'Add driver' : 'Add ambulance'}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b dark:border-gray-700 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-sky-50 dark:bg-sky-900/30">
            <TruckIcon className="h-7 w-7 text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Manage drivers &amp; ambulances
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              View fleet, add drivers and vehicles, and block or unblock. Blocked by master admin
              can only be unblocked here.
            </p>
          </div>
        </div>

        <div className="border-b border-gray-200 dark:border-gray-700 px-4">
          <nav className="flex gap-6" aria-label="Fleet tabs">
            <button
              type="button"
              onClick={() => setTab('drivers')}
              className={`py-3 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${
                tab === 'drivers'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <UserGroupIcon className="h-4 w-4" />
              Drivers ({drivers.length})
            </button>
            <button
              type="button"
              onClick={() => setTab('ambulances')}
              className={`py-3 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 ${
                tab === 'ambulances'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <TruckIcon className="h-4 w-4" />
              Ambulances ({vehicles.length})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading…</p>
          ) : tab === 'drivers' ? (
            drivers.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No drivers yet. Add a driver to get started.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Phone</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Blocked by</th>
                      <th className="py-2 pr-4">Blocked at</th>
                      <th className="py-2 pr-1 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivers.map((d) => (
                      <tr key={d.driverId} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white">{d.name}</td>
                        <td className="py-2 pr-4 font-mono">{d.phoneNumber}</td>
                        <td className="py-2 pr-4">{statusBadge(d)}</td>
                        <td className="py-2 pr-4">{blockedByLabel(d.blockedBy)}</td>
                        <td className="py-2 pr-4 text-gray-500">
                          {d.blockedAt
                            ? format(new Date(d.blockedAt), 'dd MMM yyyy, HH:mm')
                            : '—'}
                        </td>
                        <td className="py-2 pr-1 text-right whitespace-nowrap">
                          <button
                            type="button"
                            disabled={actionId === d.driverId}
                            onClick={() => toggleDriverBlock(d)}
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs border disabled:opacity-50 ${
                              d.isBlocked
                                ? 'border-green-200 text-green-700 dark:border-green-800 dark:text-green-300'
                                : 'border-red-200 text-red-700 dark:border-red-800 dark:text-red-300'
                            }`}
                          >
                            {d.isBlocked ? (
                              <>
                                <ShieldCheckIcon className="h-3.5 w-3.5" />
                                Unblock
                              </>
                            ) : (
                              <>
                                <NoSymbolIcon className="h-3.5 w-3.5" />
                                Block
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : vehicles.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No ambulances yet. Add a vehicle number plate.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-4">Number plate</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Blocked by</th>
                    <th className="py-2 pr-4">Blocked at</th>
                    <th className="py-2 pr-1 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.vehicleId} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-4 font-mono font-medium text-gray-900 dark:text-white">
                        {v.numberPlate}
                      </td>
                      <td className="py-2 pr-4">{statusBadge(v)}</td>
                      <td className="py-2 pr-4">{blockedByLabel(v.blockedBy)}</td>
                      <td className="py-2 pr-4 text-gray-500">
                        {v.blockedAt
                          ? format(new Date(v.blockedAt), 'dd MMM yyyy, HH:mm')
                          : '—'}
                      </td>
                      <td className="py-2 pr-1 text-right whitespace-nowrap">
                        <button
                          type="button"
                          disabled={actionId === v.vehicleId}
                          onClick={() => toggleVehicleBlock(v)}
                          className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs border disabled:opacity-50 ${
                            v.isBlocked
                              ? 'border-green-200 text-green-700 dark:border-green-800 dark:text-green-300'
                              : 'border-red-200 text-red-700 dark:border-red-800 dark:text-red-300'
                          }`}
                        >
                          {v.isBlocked ? (
                            <>
                              <ShieldCheckIcon className="h-3.5 w-3.5" />
                              Unblock
                            </>
                          ) : (
                            <>
                              <NoSymbolIcon className="h-3.5 w-3.5" />
                              Block
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {tab === 'drivers' ? 'Add driver' : 'Add ambulance'}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => !saving && setModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              {tab === 'drivers' ? (
                <>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Name</label>
                    <input
                      value={driverName}
                      onChange={(e) => setDriverName(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Driver full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Phone</label>
                    <input
                      value={driverPhone}
                      onChange={(e) => setDriverPhone(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="10-digit mobile"
                      maxLength={15}
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                    Number plate
                  </label>
                  <input
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                    className="w-full border rounded-md px-3 py-2 text-sm font-mono dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="MH12AB1234"
                  />
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleCreate}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      {blockDialogTarget && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center overflow-y-auto bg-black/50 p-2 sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirm block</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-700 dark:text-gray-200">
                {blockDialogTarget.type === 'driver'
                  ? `Block driver "${blockDialogTarget.row.name}"? Driver will not be able to log in or get assigned.`
                  : `Block ambulance "${blockDialogTarget.row.numberPlate}"? Vehicle cannot be assigned while blocked.`}
              </p>
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                disabled={actionId !== null}
                onClick={() => setBlockDialogTarget(null)}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionId !== null}
                onClick={confirmBlock}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white disabled:opacity-50"
              >
                {actionId !== null ? 'Blocking…' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbulanceVendorFleetPage;
